import { h, Component } from 'preact'
import getWithInMemoryCache from '../../utils/getWithInMemoryCache'
import globals from '../../utils/globals'
import { InstantSearch, Configure, Index } from 'react-instantsearch-dom'
import CustomSearchBox from './SearchBox'
import CustomCurrentRefinements from './FilterList'
import algoliasearch from 'algoliasearch'
import SearchResults from './SearchResults'
import CustomHierarchicalMenu from './filters/HierarchicalMenu'
import Icon from '../assets'
import TagsList from './filters/Tags'
import TopicType from './filters/TopicType'
import SourceType from './filters/SourceType'
import SourceTabs, { sourcesWithExternalTypes, SourceTabsType } from './filters/SourceTabs'
import qs from 'qs'
import Button from '../../control-components/ui-components/Button'
import Phrase from '../../libs/translationsUtils/phrase'
import { common } from '../common'
import { Credentials, getSearchToken, insights } from '../../services/algoliaService'
import { getFilters } from '../algolia/components/Search'

interface State {
  client: algoliasearch | null
  enableLoader: boolean
  pageCount: number
  searchState: SearchState
  isFiltersOverlay: boolean
  isKnowledgeBaseActive: boolean
  totalAmount: {
    community: number
    zendesk: number
    salesforce: number
    skilljar: number
    freshdesk: number
    totalTopics: number
  }
  sourceTabs: SourceTabsType
  listIndexes: string[]
  isZendeskFederatedSearch: boolean
  hiddenCategories: number[]
}

interface Props {
  defaultCdn: string
  attachmentCdn: string
  isGuest: boolean
  isFederatedSalesforceSearch: boolean
  isFederatedSkilljarSearch: boolean
  isFederatedFreshdeskSearch: boolean
}

const createURL = (state: unknown) => `?${qs.stringify(state) as string}`

interface SearchState {
  query: string
  refinementList?: {
    tags: string[]
    content_type: string[]
    source: string[]
  }
  hierarchicalMenu?: {
    'categories.lvl0'?: string
    'kb.categories.lvl0'?: string
  }
}

interface StateInUrl {
  q?: string
  tags?: string[]
  content_type?: string[]
  source?: string[]
  category?: string
  kb_category?: string
  search_type?: 'tag'
}

const searchStateToUrl = (searchState: SearchState) => {
  const location = window.location
  if (!searchState) return ''
  const stateToStoreInUrl: StateInUrl = {}
  if (searchState.query) {
    stateToStoreInUrl.q = searchState.query
  }
  if (searchState.refinementList?.tags) {
    stateToStoreInUrl.tags = searchState.refinementList.tags
  }

  if (searchState.hierarchicalMenu?.['categories.lvl0']) {
    stateToStoreInUrl.category = searchState.hierarchicalMenu['categories.lvl0']
  }

  if (searchState.hierarchicalMenu?.['kb.categories.lvl0']) {
    stateToStoreInUrl.kb_category = searchState.hierarchicalMenu['kb.categories.lvl0']
  }

  if (searchState.refinementList?.content_type) {
    stateToStoreInUrl.content_type = searchState.refinementList.content_type
  }

  if (searchState.refinementList?.source) {
    stateToStoreInUrl.source = searchState.refinementList.source
  }

  return `${location.pathname}${createURL(stateToStoreInUrl)}`
}

const urlToSearchState = ({ search }: Location): SearchState => {
  const stateInUrl: StateInUrl = qs.parse(search.slice(1))

  if (stateInUrl.search_type === 'tag' && stateInUrl.q) {
    return {
      query: '',
      refinementList: {
        tags: [stateInUrl.q || ''],
        content_type: [],
        source: []
      }
    }
  }

  return {
    query: stateInUrl.q ?? '',
    refinementList: {
      tags: stateInUrl.tags ?? [],
      content_type: stateInUrl.content_type ? stateInUrl.content_type : [],
      source: stateInUrl.source ? stateInUrl.source : []
    },
    hierarchicalMenu: {
      'categories.lvl0': stateInUrl.category ?? '',
      'kb.categories.lvl0': stateInUrl.kb_category ?? ''
    }
  }
}

class InstantSearchComponent extends Component<Props, State> {
  debouncedSetState: number = 0
  allIndexes: string[]
  constructor (props: Props) {
    super(props)
    const sourceTabs: Array<{
      key: sourcesWithExternalTypes
      isEnabled: boolean
    }> = [{
      key: 'community',
      isEnabled: false
    }]

    if (props.isFederatedSalesforceSearch) {
      sourceTabs.push({
        key: 'salesforce',
        isEnabled: false
      })
    }

    if (props.isFederatedSkilljarSearch) {
      sourceTabs.push({
        key: 'skilljar',
        isEnabled: false
      })
    }

    if (props.isFederatedFreshdeskSearch) {
      sourceTabs.push({
        key: 'freshdesk',
        isEnabled: false
      })
    }

    this.state = {
      client: null,
      enableLoader: false,
      pageCount: 0,
      searchState: urlToSearchState(location),
      isFiltersOverlay: false,
      totalAmount: {
        community: 0,
        zendesk: 0,
        salesforce: 0,
        skilljar: 0,
        freshdesk: 0,
        totalTopics: 0
      },
      isKnowledgeBaseActive: false,
      sourceTabs: sourceTabs,
      listIndexes: [],
      isZendeskFederatedSearch: false,
      hiddenCategories: []
    }
    this.handleStateChange = this.handleStateChange.bind(this)
    this.handleFilterChange = this.handleFilterChange.bind(this)
    this.totalTopicsAmount = this.totalTopicsAmount.bind(this)
    this.updateSources = this.updateSources.bind(this)
    this.resetSources = this.resetSources.bind(this)
    this.cb = this.cb.bind(this)
    this.isFederatedSearchEnabled = this.isFederatedSearchEnabled.bind(this)
    this.allIndexes = [`${this.communityId}`, `${this.communityId}-zendesk`, `${this.communityId}-salesforce`, `${this.communityId}-skilljar`, `${this.communityId}-freshdesk`]
  }

  communityId = globals('communityId')

  async cb (response: Credentials) {
    const client = algoliasearch(response.client_id, response.token)
    const basicClient = algoliasearch(response.client_id, response.basicToken)

    this.setState({
      client: client,
      isZendeskFederatedSearch: response.isZendeskFederatedSearchEnabled,
      hiddenCategories: response.hiddenCategories
    })

    let sourceTabs = this.state.sourceTabs

    insights('init', {
      appId: response.client_id,
      apiKey: response.token
    })

    let indexes: { items: Array<{ name: string }> } = { items: [] }
    try {
      indexes = await basicClient.listIndexes()
    } catch (e) {
      this.setState({
        listIndexes: [`${this.communityId}`, `${this.communityId}-zendesk`, `${this.communityId}-salesforce`, `${this.communityId}-skilljar`, `${this.communityId}-freshdesk`]
      })
      return
    }

    let indexNames: string[] = indexes.items.map(item => item.name)

    if (!common.isModuleActive('federatedSearchApi')) {
      indexNames = indexNames.filter((i: string) => i !== `${this.communityId}-federated`)
    }

    if (response.isZendeskFederatedSearchEnabled) {
      sourceTabs = sourceTabs.concat([{ key: 'zendesk', isEnabled: false }])
    }

    if (indexNames.includes(`${this.communityId}-federated`)) {
      sourceTabs = sourceTabs.concat([{ key: 'federated', isEnabled: false }])
    }

    this.setState({
      sourceTabs: sourceTabs,
      listIndexes: indexNames
    })
  }

  async componentDidMount () {
    await getSearchToken((c: Credentials) => {
      this.cb(c)
    })

    const knowledgeBaseResponse = await getWithInMemoryCache('/site/knowledgeBase', {})
    if (knowledgeBaseResponse?.categoryGroups.length) {
      const sourceTabs = this.state.sourceTabs
      sourceTabs.push({
        key: 'knowledge_base',
        isEnabled: false
      })
      this.setState({ isKnowledgeBaseActive: true, sourceTabs: sourceTabs })
    }

    if (this.state.searchState.refinementList?.source) {
      const enabledSources = this.state.searchState.refinementList.source
      const newTabs = this.state.sourceTabs.map(sourceTab => {
        if (enabledSources.includes(sourceTab.key)) {
          sourceTab.isEnabled = true
        }
        return sourceTab
      })
      this.setState({ sourceTabs: newTabs })
    }
  }

  handleStateChange (updatedSearchState: SearchState) {
    clearTimeout(this.debouncedSetState)

    const searchState = Object.assign(
      {},
      this.state.searchState,
      updatedSearchState
    )

    this.debouncedSetState = window.setTimeout(() => {
      history.pushState(searchState, '', searchStateToUrl(this.state.searchState))
    }, 400)

    const pageChanged = searchStateToUrl(this.state.searchState) === searchStateToUrl(searchState)

    if (!pageChanged) {
      this.setState({ pageCount: 0 })
    }

    this.setState({
      searchState: searchState
    })
  }

  handleFilterChange () {
    this.setState({
      isFiltersOverlay: !this.state.isFiltersOverlay
    })

    const bodyEl = document.querySelector('body')

    if (bodyEl) {
      if (bodyEl.style.overflow === 'hidden') {
        bodyEl.style.overflow = 'visible'
      } else {
        bodyEl.style.overflow = 'hidden'
      }
    }
  }

  totalTopicsAmount (totalAmount: State['totalAmount']) {
    if (JSON.stringify(this.state.totalAmount) !== JSON.stringify(totalAmount)) {
      this.setState({
        totalAmount: totalAmount
      })
    }
  }

  updateSources (tabs: Array<{
    key: sourcesWithExternalTypes
    isEnabled: boolean
  }>) {
    this.setState({ sourceTabs: tabs })

    const newState = this.state.searchState
    if (newState.refinementList) {
      const enabledSources = tabs.filter((sourceTab) => sourceTab.isEnabled).map((sourceTab) => sourceTab.key)
      newState.refinementList.source = enabledSources
      this.setState({ searchState: newState })
    }
  }

  resetSources () {
    this.setState({ sourceTabs: this.state.sourceTabs.map(sourceTab => { sourceTab.isEnabled = false; return sourceTab }) })
  }

  isFederatedSearchEnabled () {
    return this.state.listIndexes.includes(`${this.communityId}-federated`)
  }

  render ({ defaultCdn, attachmentCdn, isGuest, isFederatedSalesforceSearch, isFederatedSkilljarSearch, isFederatedFreshdeskSearch }: Props,
    { client, enableLoader, isFiltersOverlay, pageCount, totalAmount, isKnowledgeBaseActive, sourceTabs, listIndexes, isZendeskFederatedSearch, hiddenCategories }: State) {
    if (!client) return null

    const enabledSources: string[] = sourceTabs.filter((sourceTab) => sourceTab.isEnabled).map((sourceTab) => sourceTab.key)
    const sourceFilters: string[] = enabledSources.map(enabledSource => `source: ${enabledSource}`)

    const externalSourcesEnabled = isZendeskFederatedSearch || isFederatedSalesforceSearch || isFederatedSkilljarSearch || isFederatedFreshdeskSearch || this.isFederatedSearchEnabled()

    return (
      <div className='instant-search-wrapper'>
        <InstantSearch
          searchClient={this.state.client}
          indexName={this.communityId}
          searchState={this.state.searchState}
          onSearchStateChange={this.handleStateChange}
        >
          {
            listIndexes.includes(this.communityId)
              ? (
                <Index indexName={this.communityId}>
                  <Configure
                    hitsPerPage={(externalSourcesEnabled) ? 20 : 25}
                    facetingAfterDistinct
                    page={pageCount}
                    facetFilters={[sourceFilters]}
                    restrictSearchableAttributes={['title', 'first_post', 'answer', 'reply', 'tags', 'forum_title', 'category_title']}
                    analyticsTags={['destination', 'instant-search']}
                    clickAnalytics
                    filters={getFilters({ hiddenCategories: hiddenCategories })}
                  />
                </Index>
                )
              : null
          }
          {
            isZendeskFederatedSearch && listIndexes.includes(`${this.communityId}-zendesk`)
              ? (
                <Index indexName={`${this.communityId}-zendesk`}>
                  <Configure
                    hitsPerPage={enabledSources.length === 1 && enabledSources[0] === 'zendesk' ? 25 : 5}
                    facetFilters={[sourceFilters]}
                    page={pageCount}
                    analyticsTags={['destination', 'instant-search']}
                    clickAnalytics
                  />
                </Index>
                )
              : null
          }
          {
            isFederatedSalesforceSearch && listIndexes.includes(`${this.communityId}-salesforce`)
              ? (
                <Index indexName={`${this.communityId}-salesforce`}>
                  <Configure
                    hitsPerPage={enabledSources.length === 1 && enabledSources[0] === 'salesforce' ? 25 : 5}
                    facetFilters={[sourceFilters]}
                    page={pageCount}
                    analyticsTags={['destination', 'instant-search']}
                    clickAnalytics
                  />
                </Index>
                )
              : null
          }
          {
            isFederatedSkilljarSearch && listIndexes.includes(`${this.communityId}-skilljar`)
              ? (
                <Index indexName={`${this.communityId}-skilljar`}>
                  <Configure
                    hitsPerPage={enabledSources.length === 1 && enabledSources[0] === 'skilljar' ? 25 : 5}
                    facetFilters={[sourceFilters]}
                    page={pageCount}
                    analyticsTags={['destination', 'instant-search']}
                    clickAnalytics
                  />
                </Index>
                )
              : null
          }
          {
            isFederatedFreshdeskSearch && listIndexes.includes(`${this.communityId}-freshdesk`)
              ? (
                <Index indexName={`${this.communityId}-freshdesk`}>
                  <Configure
                    hitsPerPage={enabledSources.length === 1 && enabledSources[0] === 'freshdesk' ? 25 : 5}
                    facetFilters={[sourceFilters]}
                    page={pageCount}
                    analyticsTags={['destination', 'instant-search']}
                    clickAnalytics
                  />
                </Index>
                )
              : null
          }

          {
            this.isFederatedSearchEnabled()
              ? (
                <Index indexName={`${this.communityId}-federated`}>
                  <Configure
                    hitsPerPage={enabledSources.length === 1 && enabledSources[0] === 'federated' ? 25 : 5}
                    facetFilters={[sourceFilters]}
                    page={pageCount}
                    analyticsTags={['destination', 'instant-search']}
                    clickAnalytics
                  />
                </Index>
                )
              : null
          }
          <div className='instant-search-input-wrapper'>
            <CustomSearchBox
              defaultCdn={defaultCdn}
              enableLoader={enableLoader}
              loaderCallback={() => this.setState({ enableLoader: true })}
            />
          </div>
          <button class='instant-search__filter' onClick={this.handleFilterChange}>
            <Icon width={18} height={12} color='#6B707E' icon='filter' />
          </button>
          <div className='instant-search__results-wrapper'>
            <div class={`instant-search__results-wrapper-filter ${!isFiltersOverlay ? 'instant-search__results-wrapper-filter--hide' : ''}`}>
              <div class='instant-search__results-wrapper-filter-header'>
                <Icon icon='close' onClick={this.handleFilterChange} />
                <h3 class='instant-search__results-wrapper-filter-header-heading'>
                  <Phrase p='advanced.search.filters' />
                </h3>
                <button class='instant-search__results-wrapper-filter-header-btn'>
                  <Phrase m='Common' p='advanced.search.filter.clear.all' />
                </button>
              </div>
              <div class='instant-search__results-wrapper-filters'>
                {
                  isKnowledgeBaseActive && !externalSourcesEnabled
                    ? (
                      <SourceType
                        title={<Phrase m='Common' p='advanced.search.filter.source' />}
                        attribute='source'
                        searchable
                      />
                      )
                    : null
                }
                {
                  (externalSourcesEnabled)
                    ? (
                      <div>
                        <SourceTabs
                          title={<Phrase m='Common' p='advanced.search.filter.source' />}
                          updateSourceTabs={(tabs: Array<{
                            key: sourcesWithExternalTypes
                            isEnabled: boolean
                          }>) => this.updateSources(tabs)}
                          tabs={sourceTabs}
                        />
                        <div className='is-hidden'>
                          <SourceType
                            title={<Phrase m='Common' p='advanced.search.filter.source' />}
                            attribute='source'
                            searchable
                          />
                        </div>
                      </div>
                      )
                    : null
                }

                <TopicType
                  title={<Phrase m='Common' p='advanced.search.filter.topic.type' />}
                  attribute='content_type'
                  searchable
                />

                <CustomHierarchicalMenu
                  title={<Phrase m='Common' p='advanced.search.filter.community.category' />}
                  attributes={[
                    'categories.lvl0',
                    'categories.lvl1'
                  ]}
                />

                {
                  isKnowledgeBaseActive
                    ? (
                      <CustomHierarchicalMenu
                        title={<Phrase m='Common' p='advanced.search.filter.knowledgebase.category' />}
                        attributes={[
                          'kb.categories.lvl0',
                          'kb.categories.lvl1'
                        ]}
                      />
                      )
                    : null
                }

                <TagsList
                  title={<Phrase m='Common' p='advanced.search.filter.tag' />}
                  operator='and'
                  attribute='tags'
                  searchable
                  limit={10}
                />
              </div>
              {isFiltersOverlay &&
                <div class='instant-search__results-wrapper-filter-show-more-wrapper'>
                  <Button
                    cta
                    class='instant-search__results-wrapper-filter-show-more'
                    onClick={this.handleFilterChange}
                  >
                    <Phrase m='Forum' p='advanced.search.show.more.topics' count={{ number: totalAmount.totalTopics.toString() }} />
                  </Button>
                </div>}
            </div>

            <div class='col col--main has--side search-topic-list-wrapper'>
              <CustomCurrentRefinements resetSources={this.resetSources} />

              <SearchResults
                isGuest={isGuest}
                communityId={this.communityId}
                attachmentCdn={attachmentCdn}
                defaultCdn={defaultCdn}
                enableLoader={enableLoader}
                isZendeskFederatedSearch={isZendeskFederatedSearch}
                isFederatedSalesforceSearch={isFederatedSalesforceSearch}
                isFederatedSkilljarSearch={isFederatedSkilljarSearch}
                isFederatedFreshdeskSearch={isFederatedFreshdeskSearch}
                isFederatedSearch={this.isFederatedSearchEnabled()}
                pageCount={pageCount}
                loadNextPage={() => this.setState({ pageCount: pageCount + 1 })}
                loaderCallback={(enableLoader: boolean) => this.setState({ enableLoader: enableLoader })}
                totalTopicsAmount={this.totalTopicsAmount}
              />
            </div>
          </div>
        </InstantSearch>
      </div>
    )
  }
}

export default InstantSearchComponent
